/**
 * Командная секция профрисков в карточке заказа (canon service-prof-risks «Результат профрисков по
 * должности» + «Служебная колонка "Количество карт по итогу работ"»). Просмотр анкеты клиента
 * (read-only) + ввод результата по должностям: уровень риска + дата оценки, плюс служебная колонка
 * cardsCount (видна только команде). Уровень риска вводит вручную специалист — алгоритма нет;
 * вносить/править можно в любой момент.
 */
import { useEffect, useRef, useState } from 'react'
import { Download } from 'lucide-react'
import {
  teamApi,
  teamClientApi,
  type ProfriskAnketa,
  type ProfriskPosition,
  type ProfriskResult,
  type RiskLevel,
} from '@/lib/api'
import { getErrorMessage } from '@/lib/errors'
import { downloadBlob } from '@/lib/download'
import {
  PROFRISK_ANKETA_EXPORT_ERROR,
  RISK_LEVEL_LABELS,
  VEHICLE_USE_LABELS,
  profriskAnketaFileName,
} from '@/lib/profrisk'
import { HistoricalProfriskPanel } from './historical-profrisk-form'
import { PROFRISK_INSTRUCTIONS_TITLE, PROFRISK_METHOD_CARDS, PROFRISK_NO, PROFRISK_YES } from './profrisk-copy'
import { Card } from '@/design-system/components/layout/card'
import { Button } from '@/design-system/components/primitives/button'
import { Badge } from '@/design-system/components/primitives/badge'
import { Input } from '@/design-system/components/primitives/input'
import { Alert } from '@/design-system/components/feedback/alert'
import { ListItem } from '@/design-system/components/data/list-item'
import {
  DataTable,
  DataTableBody,
  DataTableCell,
  DataTableHead,
  DataTableHeaderCell,
  DataTableRow,
} from '@/design-system/components/data/data-table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/design-system/components/primitives/select'

const RISK_LEVELS: RiskLevel[] = ['high', 'medium', 'acceptable', 'not_assessed']

interface RowDraft {
  riskLevel: RiskLevel
  assessedOn: string
  cardsCount: string
}

/** Сводка блока инструкций по ОТ для командного просмотра анкеты (centrika-docs!25). */
function instructionsSummary(anketa: ProfriskAnketa): string {
  if (anketa.instructionsDeveloped === 'no') return PROFRISK_NO
  if (anketa.instructionsDeveloped !== 'yes') return '—'
  if (anketa.instructionsMethod === 'files') {
    return `${PROFRISK_YES} · ${PROFRISK_METHOD_CARDS.files.title} (${anketa.instructionFiles.length} шт.)`
  }
  if (anketa.instructionsMethod === 'manual') {
    const count = anketa.instructions.filter((ins) => ins.name.trim() !== '').length
    return `${PROFRISK_YES} · ${PROFRISK_METHOD_CARDS.manual.title} (${count} шт.)`
  }
  return `${PROFRISK_YES} · способ не выбран`
}

/**
 * Кнопка «Скачать в Excel» (profrisk-anketa-export-template) — первой в карточке анкеты клиента:
 * файл по шаблону заказчика формирует СЕРВЕР по сохранённым данным анкеты, по образцу выгрузки
 * рабочих мест СОУТ. Пока файл формируется, кнопка недоступна и показывает «Формирование файла…»;
 * при неудаче рядом с кнопкой появляется сообщение, а кнопка снова доступна.
 */
function AnketaExportButton({ orderId, orderNumber }: { orderId: string; orderNumber: string }) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function download() {
    setError(null)
    setBusy(true)
    try {
      downloadBlob(
        profriskAnketaFileName(orderNumber),
        await teamApi.profrisk.downloadAnketa(orderId),
      )
    } catch {
      setError(PROFRISK_ANKETA_EXPORT_ERROR)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="mb-3 flex flex-wrap items-center gap-3">
      <Button
        size="sm"
        variant="secondary"
        disabled={busy}
        onClick={() => void download()}
        leftIcon={<Download className="size-4 stroke-current" aria-hidden />}
      >
        {busy ? 'Формирование файла…' : 'Скачать в Excel'}
      </Button>
      {error ? <span className="text-12 text-red">{error}</span> : null}
    </div>
  )
}

/** Ключ карты «должность заказа → рабочее место структуры» (подразделение+должность). */
function workplaceKey(departmentName: string, position: string): string {
  return `${departmentName}\u0000${position}`
}

export function TeamProfriskSection({ orderId }: { orderId: string }) {
  const [anketa, setAnketa] = useState<ProfriskAnketa | null>(null)
  const [results, setResults] = useState<ProfriskResult[]>([])
  const [drafts, setDrafts] = useState<Record<string, RowDraft>>({})
  // p2-manual-historical (§11.3): должность заказа — текст (department+position),
  // без ссылки на workplace_id структуры. Историч. ввод требует именно workplaceId
  // (design D5), поэтому резолвим его по той же паре (подразделение, должность) из
  // структуры клиента-владельца заказа (best-effort — не находится, если структура
  // изменилась/должности нет в структуре; тогда точка входа просто не показывается).
  const [workplaceByKey, setWorkplaceByKey] = useState<Record<string, string>>({})
  // Номер заказа — для имени файла выгрузки анкеты («Анкета профрисков — ОПР-12.xlsx»).
  const [orderNumber, setOrderNumber] = useState('')
  // Отменённый заказ только просматривается (order-cancel-restore): результаты — после восстановления.
  const [orderCancelled, setOrderCancelled] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  // Гард от апдейта после размонтирования: post-save load() идёт без AbortSignal.
  const mountedRef = useRef(true)

  function load(signal?: AbortSignal) {
    const cancelled = () => Boolean(signal?.aborted) || !mountedRef.current
    setLoading(true)
    setError(null)
    Promise.all([
      teamApi.profrisk.getAnketa(orderId, signal),
      teamApi.profrisk.getResults(orderId, signal),
      teamApi.orders.get(orderId, signal),
    ])
      .then(([ank, res, order]) => {
        if (cancelled()) return
        setAnketa(ank)
        setResults(res)
        setOrderNumber(order.number)
        setOrderCancelled(order.dataStatus === 'cancelled')
        const next: Record<string, RowDraft> = {}
        for (const pos of ank.positions) {
          const existing = res.find((r) => r.positionId === pos.id)
          next[pos.id] = {
            riskLevel: existing?.riskLevel ?? 'not_assessed',
            assessedOn: existing?.assessedOn ?? '',
            cardsCount: pos.cardsCount != null ? String(pos.cardsCount) : '',
          }
        }
        setDrafts(next)
        teamClientApi(order.ownerClientId)
          .listWorkplaces()
          .then((wpRes) => {
            if (cancelled()) return
            const map: Record<string, string> = {}
            for (const w of wpRes.items) {
              map[workplaceKey(w.departmentName, w.position)] = w.id
            }
            setWorkplaceByKey(map)
          })
          .catch(() => undefined)
      })
      .catch((err) => {
        if (!cancelled()) setError(getErrorMessage(err, 'Не удалось загрузить анкету профрисков'))
      })
      .finally(() => {
        if (!cancelled()) setLoading(false)
      })
  }

  useEffect(() => {
    mountedRef.current = true
    const controller = new AbortController()
    load(controller.signal)
    return () => {
      mountedRef.current = false
      controller.abort()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId])

  function setDraft(posId: string, patch: Partial<RowDraft>) {
    setDrafts((prev) => ({ ...prev, [posId]: { ...prev[posId], ...patch } }))
  }

  async function save() {
    if (!anketa) return
    setSaveError(null)
    setSaving(true)
    try {
      // Служебная колонка cardsCount — отдельным PATCH по должностям, где значение изменилось.
      // Запросы независимы → шлём параллельно (Promise.all) вместо N последовательных round-trip.
      const cardUpdates = anketa.positions
        .map((pos) => {
          const draft = drafts[pos.id]
          if (!draft) return null
          const parsed = draft.cardsCount.trim() === '' ? null : Number(draft.cardsCount)
          if (parsed != null && Number.isNaN(parsed)) return null
          if (parsed === pos.cardsCount) return null
          return teamApi.profrisk.updatePosition(orderId, pos.id, { cardsCount: parsed })
        })
        .filter((p): p is Promise<ProfriskPosition> => p !== null)
      await Promise.all(cardUpdates)
      // Результат — upsert по должностям с проставленной датой оценки.
      const items = anketa.positions
        .map((pos) => ({ pos, draft: drafts[pos.id] }))
        .filter(({ draft }) => draft && draft.assessedOn)
        .map(({ pos, draft }) => ({
          positionId: pos.id,
          riskLevel: draft.riskLevel,
          assessedOn: draft.assessedOn,
        }))
      await teamApi.profrisk.putResults(orderId, { items })
      // Перечитываем анкету (cardsCount) и результаты, чтобы освежить черновики.
      load()
    } catch (err) {
      setSaveError(getErrorMessage(err, 'Не удалось сохранить результаты'))
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <Card header="Анкета профрисков" className="mt-4">
        <p className="py-2 text-txt-secondary">Загрузка…</p>
      </Card>
    )
  }
  if (error || !anketa) {
    return (
      <Alert variant="danger" className="mt-4">
        {error ?? 'Анкета недоступна'}
      </Alert>
    )
  }

  // Гейт §8.4/§5 (design D4): «Завершить» заблокирован, пока хотя бы по одной должности
  // нет сохранённого результата (риск ≠ not_assessed И дата оценки заполнена). Считаем
  // по загруженным результатам (не по несохранённым черновикам) — тот же критерий, что
  // и серверный гейт, чтобы 409 не был неожиданным.
  const missingCount = anketa.positions.filter((pos) => {
    const r = results.find((x) => x.positionId === pos.id)
    return !r || r.riskLevel === 'not_assessed' || !r.assessedOn
  }).length

  return (
    <div className="mt-4 flex flex-col gap-4">
      <Card header="Анкета профрисков (данные клиента)">
        <AnketaExportButton orderId={orderId} orderNumber={orderNumber} />
        <p className="mb-2 text-13 text-txt-secondary">
          Контакт: {anketa.contact.fullName || '—'}
          {anketa.contact.phone ? ` · ${anketa.contact.phone}` : ''}
          {anketa.contact.email ? ` · ${anketa.contact.email}` : ''}
        </p>
        {/* Блок «Разработанные инструкции по охране труда» (centrika-docs!25): менеджеру нужны
            ответ клиента, выбранный способ передачи и сами переданные данные (файлы — ссылками,
            ручной список — наименованиями). Блока «Подтверждение анкеты» больше нет. */}
        <p className="mb-2 text-13 text-txt-secondary">
          {PROFRISK_INSTRUCTIONS_TITLE}: {instructionsSummary(anketa)}
        </p>
        {anketa.instructionsDeveloped === 'yes' && anketa.instructionsMethod === 'files' ? (
          <ul className="mb-3 ml-4 list-disc text-13">
            {anketa.instructionFiles.map((f) => (
              <li key={f.id}>
                <a className="text-accent underline" href={f.url} download>
                  {f.name}
                </a>
              </li>
            ))}
          </ul>
        ) : null}
        {anketa.instructionsDeveloped === 'yes' && anketa.instructionsMethod === 'manual' ? (
          <ul className="mb-3 ml-4 list-disc text-13 text-txt-secondary">
            {anketa.instructions
              .filter((ins) => ins.name.trim() !== '')
              .map((ins, i) => (
                <li key={`${i}-${ins.name}`}>{ins.name}</li>
              ))}
          </ul>
        ) : null}
        {anketa.positions.length === 0 ? (
          <p className="py-2 text-txt-secondary">Должности не заполнены.</p>
        ) : (
          anketa.positions.map((pos, i) => (
            <ListItem
              key={pos.id}
              name={`№${i + 1} · ${pos.position || 'должность не указана'}`}
              meta={`${pos.departmentName || '—'} · штатных единиц: ${pos.headcount} · выезды: ${
                VEHICLE_USE_LABELS[pos.vehicleUse]
              }`}
            />
          ))
        )}
      </Card>

      <Card header="Результат профрисков по должностям">
        {saveError ? (
          <Alert variant="danger" className="mb-3">
            {saveError}
          </Alert>
        ) : null}
        {missingCount > 0 && anketa.positions.length > 0 ? (
          <p className="mb-3 flex items-center gap-2 text-13 text-txt-secondary">
            <Badge status="warning">не оценено: {missingCount} из {anketa.positions.length}</Badge>
            Завершить заказ можно только после результата по всем должностям.
          </p>
        ) : null}
        {anketa.positions.length === 0 ? (
          <p className="py-2 text-txt-secondary">Нет должностей для ввода результата.</p>
        ) : (
          <>
            <DataTable>
              <DataTableHead>
                <tr>
                  <DataTableHeaderCell>Должность</DataTableHeaderCell>
                  <DataTableHeaderCell>Уровень риска</DataTableHeaderCell>
                  <DataTableHeaderCell>Дата оценки</DataTableHeaderCell>
                  <DataTableHeaderCell>Карт по итогу</DataTableHeaderCell>
                  <DataTableHeaderCell>Историческая оценка</DataTableHeaderCell>
                </tr>
              </DataTableHead>
              <DataTableBody>
                {anketa.positions.map((pos, i) => {
                  const draft = drafts[pos.id] ?? {
                    riskLevel: 'not_assessed',
                    assessedOn: '',
                    cardsCount: '',
                  }
                  return (
                    <DataTableRow key={pos.id}>
                      <DataTableCell className="font-medium text-txt">
                        №{i + 1} · {pos.position || '—'}
                      </DataTableCell>
                      <DataTableCell>
                        <Select
                          value={draft.riskLevel}
                          onValueChange={(v) => setDraft(pos.id, { riskLevel: v as RiskLevel })}
                        >
                          <SelectTrigger aria-label={`Уровень риска должности №${i + 1}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {RISK_LEVELS.map((lvl) => (
                              <SelectItem key={lvl} value={lvl}>
                                {RISK_LEVEL_LABELS[lvl]}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </DataTableCell>
                      <DataTableCell>
                        <Input
                          type="date"
                          value={draft.assessedOn}
                          aria-label={`Дата оценки должности №${i + 1}`}
                          onChange={(e) => setDraft(pos.id, { assessedOn: e.target.value })}
                        />
                      </DataTableCell>
                      <DataTableCell>
                        <Input
                          type="number"
                          min={0}
                          value={draft.cardsCount}
                          aria-label={`Количество карт по итогу должности №${i + 1}`}
                          onChange={(e) => setDraft(pos.id, { cardsCount: e.target.value })}
                          placeholder="—"
                        />
                      </DataTableCell>
                      <DataTableCell>
                        {/* p2-manual-historical (§11.3): доступно «в любой момент», независимо
                            от результата/статуса заказа (design D4). Показывается, только если
                            должность резолвится в реальное рабочее место структуры (design D5). */}
                        {workplaceByKey[workplaceKey(pos.departmentName, pos.position)] ? (
                          <HistoricalProfriskPanel
                            workplaceId={workplaceByKey[workplaceKey(pos.departmentName, pos.position)]}
                            canUploadDocument
                          />
                        ) : (
                          '—'
                        )}
                      </DataTableCell>
                    </DataTableRow>
                  )
                })}
              </DataTableBody>
            </DataTable>
            <p className="mt-3 text-12 text-txt-tertiary">
              Уровень риска вносит специалист вручную; правится в любой момент. «Карт по итогу» —
              служебная колонка, клиенту не видна.
            </p>
            {orderCancelled ? (
              <p className="mt-3 text-14 text-txt-secondary">
                Заказ отменён — результаты сохраняются после восстановления заказа.
              </p>
            ) : (
              <Button className="mt-3" disabled={saving} onClick={() => void save()}>
                {saving ? 'Сохранение…' : 'Сохранить результаты'}
              </Button>
            )}
          </>
        )}
      </Card>
    </div>
  )
}
